import { IS_BROWSER } from "@config/consts";
import { ROUTES } from "@config/app.routing";

import { buildUrl } from "@services/BuildUrl";
import { ASYNC_ACTION_TYPES } from "@services/FetchFacade";
import { stringifyFalsies } from "@services/StringifyFalsies";
import { safelyAddQuery } from "@services/SafelyAddQuery";

export enum PAYMENT_STATUS_RETURN_TYPE {
  SUCCESS = "SUCCESS",
  ERROR = "ERROR",
  CANCEL = "CANCEL",
}

interface ProvideSuccessUrl {
  referenceNumber: string;
  merchantId: string;
  amount: string;
  currency: string;
  originRoute: string;
  targetRoute: string;
  paymentProcessedRoute: string;
}

export const provideSuccessUrl = ({
  referenceNumber,
  merchantId,
  amount,
  currency,
  originRoute,
  targetRoute,
  paymentProcessedRoute,
}: ProvideSuccessUrl): string => {
  if (IS_BROWSER) {
    const originWPathName = `${window.location.origin}${window.location.pathname}`;

    const referenceNumberQueryParam = `referenceNumber=${referenceNumber}`;
    const merchantIdQueryParam = `merchantId=${merchantId}`;
    const amountQueryParam = `amount=${amount}`;
    const currencyQueryParam = `currency=${currency}`;
    const paymentStatusQueryParam = `paymentStatus=${PAYMENT_STATUS_RETURN_TYPE.SUCCESS}`;
    const originRouteQueryParam = `originRoute=${encodeURIComponent(
      originRoute,
    )}`;
    const targetRouteQueryParam = `targetRoute=${encodeURIComponent(
      targetRoute,
    )}`;

    const queryParams = `?${referenceNumberQueryParam}&${merchantIdQueryParam}&${amountQueryParam}&${currencyQueryParam}&${paymentStatusQueryParam}&${originRouteQueryParam}&${targetRouteQueryParam}`;

    const successUrl = `${originWPathName}#${paymentProcessedRoute}${queryParams}`;

    return successUrl;
  }

  return "/";
};

export const provideFailureUrl = (
  messageType: PAYMENT_STATUS_RETURN_TYPE,
): string => {
  if (IS_BROWSER) {
    const href = window.location.href;

    return safelyAddQuery(href, `paymentStatus=${messageType}`);
  }

  return "/";
};

export const provideUrlsFromHref = (
  href: string | null,
  originRoute: string,
  targetRoute: string,
): string => {
  if (typeof href !== "string") {
    return stringifyFalsies(href);
  }

  if (!href.includes("&")) {
    return href;
  }

  const paymentProcessedRoute = ROUTES.PAYMENT_PROCESSED;
  const location = window.location.href;
  const sourceParams = href.split("&");
  const targetParams = [...sourceParams];

  const cancelUrlIndex = sourceParams.findIndex(item =>
    item.includes("cancelUrl"),
  );
  const errorUrlIndex = sourceParams.findIndex(item =>
    item.includes("errorUrl"),
  );
  const successUrlIndex = sourceParams.findIndex(item =>
    item.includes("successUrl"),
  );
  const uppWebResponseMethodIndex = sourceParams.find(item =>
    item.includes("uppWebResponseMethod"),
  );

  if (!uppWebResponseMethodIndex) {
    targetParams.push(`uppWebResponseMethod=${ASYNC_ACTION_TYPES.GET}`);
  }

  const referenceNumber = String(
    sourceParams.find(item => item.includes("refno")),
  ).split("=")[1];

  const referenceNumberQueryParam = `referenceNumber=${referenceNumber}`;
  const paymentStatusQueryParam = `paymentStatus=${PAYMENT_STATUS_RETURN_TYPE.SUCCESS}`;
  const originRouteQueryParam = `originRoute=${encodeURIComponent(
    originRoute,
  )}`;
  const targetRouteQueryParam = `targetRoute=${encodeURIComponent(
    targetRoute,
  )}`;

  const successQueryParams = `?${referenceNumberQueryParam}&${paymentStatusQueryParam}&${originRouteQueryParam}&${targetRouteQueryParam}`;

  const target = `${window.location.origin}${window.location.pathname}#${paymentProcessedRoute}${successQueryParams}`;

  targetParams[cancelUrlIndex] = `cancelUrl=${encodeURIComponent(
    safelyAddQuery(
      location,
      `paymentStatus=${PAYMENT_STATUS_RETURN_TYPE.CANCEL}`,
    ),
  )}`;
  targetParams[errorUrlIndex] = `errorUrl=${encodeURIComponent(
    safelyAddQuery(
      location,
      `paymentStatus=${PAYMENT_STATUS_RETURN_TYPE.ERROR}`,
    ),
  )}`;

  targetParams[successUrlIndex] = `successUrl=${encodeURIComponent(target)}`;

  return targetParams.join("&");
};

interface GenerateReturnUrl {
  originRoute: string;
  targetRoute: string;
  reservationUuid?: string;
  options?: {
    encodeOriginRoute?: boolean;
    encodeTargetRoute?: boolean;
  };
}

export const generateReturnUrl = ({
  originRoute,
  targetRoute,
  reservationUuid,
  options,
}: GenerateReturnUrl): string => {
  const url = buildUrl(
    `${window.location.origin}${window.location.pathname}#${ROUTES.PAYMENT_PROCESSED}`,
  );

  url.addQuery("originRoute", originRoute, {
    encode: !!options?.encodeOriginRoute,
  });
  url.addQuery("targetRoute", targetRoute, {
    encode: !!options?.encodeTargetRoute,
  });

  if (reservationUuid) {
    url.addQuery("reservationUuid", reservationUuid);
  }

  return url.done();
};

interface QueryValues {
  referenceNumber: string;
  merchantId: string;
  amount: string;
  currency: string;
}

export const attachFakePaymentWebhookQueries = (
  url: string,
  queryValues: QueryValues,
): string => {
  return buildUrl(url)
    .addQuery("referenceNumber", queryValues.referenceNumber)
    .addQuery("merchantId", queryValues.merchantId)
    .addQuery("amount", queryValues.amount)
    .addQuery("currency", queryValues.currency)
    .done();
};
